/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `participantScheduleValidation` for the Caintra Connect frontend.
 * @module utils/participantScheduleValidation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */

export const emptySchedule = () => ({
  fecha: "",
  horaInicio: "",
  horaFin: "",
  horaInicioComida: "",
  horaFinComida: "",
});

export const normalizeCustomerUser = (entry) => ({
  _id: entry._id,
  fecha: entry.fecha ?? "",
  horaInicio: entry.horaInicio ?? "",
  horaFin: entry.horaFin ?? "",
  horaInicioComida: entry.horaInicioComida ?? "",
  horaFinComida: entry.horaFinComida ?? "",
});

export const toHHmm = (time) => {
  if (time == null || typeof time !== "string") {
    return "";
  }
  const trimmed = time.trim();
  if (!trimmed) {
    return "";
  }
  const parts = trimmed.split(":");
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10) || 0;
  if (Number.isNaN(hour)) {
    return trimmed.slice(0, 5);
  }
  const h = String(hour).padStart(2, "0");
  const m = String(minute).padStart(2, "0");
  return `${h}:${m}`;
};

export const isTimeWithinRange = (value, rangeStart, rangeEnd) => {
  if (!value || !rangeStart || !rangeEnd) {
    return true;
  }
  const v = toHHmm(value);
  const s = toHHmm(rangeStart);
  const e = toHHmm(rangeEnd);
  return v >= s && v <= e;
};

const EMPTY_VALIDATION = {
  fecha: false,
  horaInicio: false,
  horaFin: false,
  horaInicioComida: false,
  horaFinComida: false,
};

export const getScheduleValidation = (
  entry,
  { eventDateStart, eventDateEnd, eventTimeStart, eventTimeEnd },
) => {
  const fecha = (entry.fecha ?? "").trim();
  const horaInicio = (entry.horaInicio ?? "").trim();
  const horaFin = (entry.horaFin ?? "").trim();
  const horaInicioComida = (entry.horaInicioComida ?? "").trim();
  const horaFinComida = (entry.horaFinComida ?? "").trim();

  const fechaInvalid =
    Boolean(eventDateStart && eventDateEnd) &&
    fecha !== "" &&
    (fecha < eventDateStart || fecha > eventDateEnd);

  const horaInicioInvalid =
    Boolean(eventTimeStart && eventTimeEnd) &&
    horaInicio !== "" &&
    !isTimeWithinRange(horaInicio, eventTimeStart, eventTimeEnd);

  const horaFinInvalid =
    Boolean(eventTimeStart && eventTimeEnd) &&
    horaFin !== "" &&
    (!isTimeWithinRange(horaFin, eventTimeStart, eventTimeEnd) ||
      (horaInicio !== "" && horaFin <= horaInicio));

  const horaInicioComidaInvalid =
    Boolean(eventTimeStart && eventTimeEnd) &&
    horaInicioComida !== "" &&
    !isTimeWithinRange(horaInicioComida, eventTimeStart, eventTimeEnd);

  const horaFinComidaInvalid =
    Boolean(eventTimeStart && eventTimeEnd) &&
    horaFinComida !== "" &&
    (!isTimeWithinRange(horaFinComida, eventTimeStart, eventTimeEnd) ||
      (horaInicioComida !== "" && horaFinComida <= horaInicioComida));

  return {
    fecha: fechaInvalid,
    horaInicio: horaInicioInvalid,
    horaFin: horaFinInvalid,
    horaInicioComida: horaInicioComidaInvalid,
    horaFinComida: horaFinComidaInvalid,
  };
};

export const hasScheduleValidationError = (validation) =>
  validation.fecha ||
  validation.horaInicio ||
  validation.horaFin ||
  validation.horaInicioComida ||
  validation.horaFinComida;

export const getEmptyScheduleValidation = () => ({ ...EMPTY_VALIDATION });
